import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  useCreateContract,
  CreateContractValidation,
} from "@/lib/api/otc";
import { UpdateContractInfo } from "@/section/otc/OtcBearState";

interface UseOtcContractFormHandlersProps {
  refetchContracts: () => void;
  onCloseModal: () => void;
}

export function useOtcContractFormHandlers({
  refetchContracts,
  onCloseModal,
}: UseOtcContractFormHandlersProps) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const createContract = useCreateContract();

  const onSubmitForm = async (
    formData: UpdateContractInfo,
    isUpdateForm: boolean,
  ): Promise<boolean> => {
    if (isSaving) return false;

    // Existing-contract edits no longer go through this handler: the deprecated
    // generic update route (PUT /otc/contracts/:id) has been removed in favour
    // of the layered editor in OtcContractDetailModal. This handler is only
    // mounted for net-new contract creation.
    if (isUpdateForm) {
      toast({
        title: "Editing moved",
        description:
          "Edit existing OTC contracts from the contract detail view, not this form.",
      });
      return false;
    }

    setIsSaving(true);

    // The V2 form handles validation and data transformation (including
    // canola derived fields) before calling onSubmit, so formData is
    // already API-ready.
    const data = JSON.parse(JSON.stringify(formData));

    const saveResult = await createContract(
      data as typeof CreateContractValidation._type,
    );

    setIsSaving(false);

    if (!saveResult.success) {
      toast({ title: "Failed to create contract", description: "" });
    } else {
      onCloseModal();
      refetchContracts();
      toast({ title: "Created", description: "" });
    }

    return true;
  };

  return {
    onSubmitForm,
    isSaving,
    createContract,
  };
}

